import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { DatabaseBackup } from 'lucide-react'
import { useCarWash } from '@/hooks/use-carwash'
import { Button } from '@/components/kit/button'
import { PageSkeleton } from '@/components/kit/skeleton'
import { PageContainer } from '@/components/app/page-header'

/*
 * Renders its children only once the saved data has been read on this
 * device. Until then it shows a skeleton, and if the saved data cannot be
 * read it offers a way to start again instead of a blank screen.
 */
export const DataGate: FC<{ children: ReactNode, skeleton?: ReactNode }> = ({ children, skeleton }): ReactNode => {
    const { status, restoreDemo, startFresh } = useCarWash()
    const [busy, setBusy] = useState<'demo' | 'fresh' | null>(null)

    if (status === 'loading') {
        return skeleton ?? (
            <PageContainer>
                <PageSkeleton />
            </PageContainer>
        )
    }

    if (status === 'error') {
        return (
            <PageContainer>
                <div className='mx-auto max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-card'>
                    <span className='mx-auto grid size-12 place-items-center rounded-full bg-red-50 text-red-600'>
                        <DatabaseBackup className='size-6' aria-hidden='true' />
                    </span>

                    <h2 className='mt-4 font-heading text-lg font-bold text-foreground'>
                        Saved data could not be opened
                    </h2>

                    <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                        The records saved on this device are damaged. Start again with sample data, or with an empty car wash that keeps the standard services and staff.
                    </p>

                    <div className='mt-6 grid gap-3 sm:grid-cols-2'>
                        <Button
                            variant='secondary'
                            size='lg'
                            loading={busy === 'fresh'}
                            onClick={async () => {
                                setBusy('fresh')
                                await startFresh()
                                setBusy(null)
                            }}
                        >
                            Start empty
                        </Button>

                        <Button
                            size='lg'
                            loading={busy === 'demo'}
                            onClick={async () => {
                                setBusy('demo')
                                await restoreDemo()
                                setBusy(null)
                            }}
                        >
                            Load sample data
                        </Button>
                    </div>
                </div>
            </PageContainer>
        )
    }

    return <>{children}</>
}
